import {
  Box,
  Button,
  Dialog,
  Drawer,
  Grid,
  IconButton,
  Paper,
} from '@mui/material';
import { Header } from '../components/header';
import { useTranslation } from 'react-i18next';
import { Text } from '../elements/text';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Image } from '../elements/image';
import { getOrderTrackingRoute, VIEW_ALL_ORDERS } from '../constants/routes';
import { GET_PATRON_ORDER, GET_VENUE } from '../graphql/query';
import { Loader } from '../elements/loader';
import { useContext, useState } from 'react';
import {
  OrderStatus,
  PatronOrder,
  PatronOrderStatus,
  VendorOrder,
} from '../types/order';
import { Border } from '../elements/border';
import { getPriceWithCurrency } from '../utils/charges';
import { useEffect } from 'react';
import { OrderContext } from '../context/order-context';
import { useQueryHook } from '../hooks/use-query.hook';
import { NotificationContext } from '../context/notification-context';
import { CartContext, CartItem } from '../context/cart-context';
import { useMutationHook } from '../hooks/use-mutation';
import { CANCEL_ORDER } from '../graphql/mutation';
import { CustomerSupportButton } from '../components/customer-support-button';
import { NetworkErrorPage } from './network-error';
import { ReactComponent as SneakerIcon } from '../assets/icons/SneakerSticker.svg';
import { AppContext } from '../context/app-context';
import { CollectionPoint, Venue } from '../types';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { OrderTrackingInfo } from '../components/order-tracking-page/order-tracking-card';
import { SaveAppToPhoneButton } from '../components/save-app-to-phone';
import { GlobalHomeButton } from './global-home-button';
import { VendorActionButton } from './vendor-action-button';
import { isCollectionOrder } from '../utils/order-status-helpers';

export const OrderTracking = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(['patron_portal', 'common']);

  const { orderID = '' } = useParams();
  const [searchParams] = useSearchParams();
  const notificationContext = useContext(NotificationContext);
  const cartContext = useContext(CartContext);
  const { venueType, serviceMethod, sections, section } =
    useContext(AppContext);
  const orderContext = useContext(OrderContext);
  const [selectedOrderNavigationImage, setSelectedOrderNavigationImage] =
    useState('');

  const {
    data,
    loading: initialLoading,
    fetch: fetchGetPatronOrder,
    error: patronOrderError,
    errorObject,
  } = useQueryHook({
    query: GET_PATRON_ORDER,
    variables: {
      id: orderID,
    },
    onCompleted: (data) => {
      const location = data?.getPatronOrder.location;
      fetchVenueData({
        id: location?.venue,
      });
    },
  });

  const TIME_INTERVAL_IN_SECS = 60;
  const reloadOrder = async (payload = {}, isAutoReload = false) => {
    if (isAutoReload) {
      let orderLastRefreshedAt = sessionStorage.getItem('orderLastRefreshedAt');
      if (!orderLastRefreshedAt) {
        sessionStorage.setItem('orderLastRefreshedAt', `${Date.now()}`);
        orderLastRefreshedAt =
          sessionStorage.getItem('orderLastRefreshedAt') || '';
      }
      if (
        Math.floor((Date.now() - parseInt(orderLastRefreshedAt)) / 1000) <
        TIME_INTERVAL_IN_SECS
      ) {
        return;
      }
    }
    await fetchGetPatronOrder(payload);
    sessionStorage.setItem('orderLastRefreshedAt', `${Date.now()}`);
  };

  useEffect(() => {
    history.pushState('view-all-orders', '', VIEW_ALL_ORDERS);
  }, []);

  useEffect(() => {
    const interval = setInterval(async () => {
      await fetchGetPatronOrder(
        {
          id: orderID,
        },
        true,
      );
    }, (TIME_INTERVAL_IN_SECS + 1) * 1000);
    return () => clearInterval(interval);
  }, []);

  const { mutate: cancelOrder, loading: cancelOrderLoading } = useMutationHook({
    mutation: CANCEL_ORDER,
    onCompleted: () => {
      reloadOrder({
        id: orderID,
      });
    },
  });

  useEffect(() => {
    if (
      searchParams.get('payment_intent') ||
      searchParams.get('payment_intent_client_secret')
    ) {
      orderContext.clear();
      cartContext.clear();

      navigate(getOrderTrackingRoute(orderID));
    }

    notificationContext.addSubcribers({
      id: 'order-tracking-fetch-order',
      call: () => {
        reloadOrder({ id: orderID });
      },
    });

    return () => {
      notificationContext.clear();
    };
  }, []);

  const order: PatronOrder = data?.getPatronOrder;
  const location = order?.location;
  const vendorOrders = order?.vendorOrders || [];
  const shouldHideHomeButton = vendorOrders.every((vendorOrder) =>
    isCollectionOrder([vendorOrder], venueType?.value, serviceMethod?.value),
  );
  const {
    loading: fetchingVenue,
    data: venueData,
    fetch: fetchVenueData,
  } = useQueryHook({
    query: GET_VENUE,
    variables: {
      id: location?.venue,
    },
    skipInitialCall: true,
  });
  const venue: Venue = venueData?.getVenue;

  const selectedCollectionPoint = venue?.collectionPoints?.find(
    (_collectionPoint: CollectionPoint) =>
      _collectionPoint?.id === location?.collectionPoint,
  );

  const getCancellationStatus = () => {
    if (
      order?.orderStatus in
      [PatronOrderStatus.REJECTED, PatronOrderStatus.REFUNDED]
    )
      return false;
    const ordersWithoutRejected = vendorOrders.filter(
      (vendorOrder) =>
        !(
          vendorOrder.orderStatus in
          [OrderStatus.REJECTED, OrderStatus.REFUNDED]
        ),
    );
    const changedVendorOrder = ordersWithoutRejected.find(
      (vendorOrder) => vendorOrder.orderStatus !== OrderStatus.RECEIVED,
    );
    if (changedVendorOrder) return false;
    return true;
  };

  const canCancel = getCancellationStatus();
  const cancelOrderHandler = () => {
    cancelOrder({
      variables: {
        id: orderID,
      },
    });
  };

  if (initialLoading || fetchingVenue) {
    return <Loader visible={true} />;
  }

  const itemsPaymentFailed = () => {
    const paymentFailedOrders = vendorOrders.filter(
      (vendorOrder) => vendorOrder.orderStatus === OrderStatus.PAYMENT_REJECTED,
    );

    const itemsToCart: CartItem[] = paymentFailedOrders.flatMap((vendorOrder) =>
      vendorOrder.items.map((item) => ({
        id: item._id,
        name: item.name,
        count: item.count,
        menuID: item.menuID,
        customisations: item.customisations?.map(
          (customisation) => customisation._id,
        ),
      })),
    );
    return itemsToCart;
  };

  const selectedSectionLabel = sections?.value?.find(
    (sec) => sec.id === section?.value,
  )?.label;

  const getSectionName = () => {
    return [
      {
        key: 'patron_portal.order.msgLocationSection',
        params: { sectionName: selectedSectionLabel },
      },
    ];
  };

  const header = (
    <Grid item xs={12}>
      <Header
        backIconRoute={VIEW_ALL_ORDERS}
        middleComponent={
          <div>
            <Text
              fontSize={18}
              fontWeight={700}
              text={t('patron_portal.order.labelMyOrder', {
                ns: 'patron_portal',
              })}
            />
            {getSectionName().map(({ key, params }) => (
              <Text
                key={key}
                fontSize={14}
                fontWeight={500}
                text={t(key, { ns: 'patron_portal', ...params })}
                mt={0.5}
              />
            ))}
          </div>
        }
      />
    </Grid>
  );

  const vendorOrderStatuses = order?.vendorOrders?.map(
    (vendorOrder) => vendorOrder.orderStatus,
  );

  // Here we create a mapping between the vendor order IDs and their
  // navigation map URIs in order to retrieve the correct navigation map URI
  // on click of the relevant Vendor Order's "Show Directions" button.
  // We do this here as the service method is at the PatronOrder level, not the
  // VendorOrder level.
  const vendorOrderNavigationMap: Map<string, string> = new Map<string, string>(
    (order?.vendorOrders || []).map((vendorOrder) => [
      vendorOrder._id,
      vendorOrder.store?.navigationMapUri ||
        vendorOrder.vendor?.navigationMapUri ||
        venue?.sections.find((section) => section.id === order.location.section)
          ?.cncNavigationImageUrl ||
        venue?.venueMapImageUrl ||
        venue?.patronNavigationImageUrl ||
        '',
    ]),
  );

  return (
    <Grid
      container
      xs={12}
      sm={8}
      md={6}
      lg={3}
      margin={'auto'}
      //styling to mock mobile borders on desktop - TBD
      sx={{
        border: { lg: '2px solid #CECECE' },
        borderRadius: { lg: '54px' },
        background: '#F5F5F5',
        padding: { lg: '8px 12px' },
        boxShadow: {
          lg: 'inset 0 0 2px 2px dimgrey, inset 0 0 0 6px #e2e3e9, 0px -1px 13px 9px rgba(255,255,255,0.8) inset',
        },
      }}
    >
      <DirectionModal
        isModalOpen={selectedOrderNavigationImage !== ''}
        onClose={() => setSelectedOrderNavigationImage('')}
        image={selectedOrderNavigationImage}
      />
      <Loader visible={cancelOrderLoading} />
      {header}

      {patronOrderError && errorObject?.networkError && (
        <Dialog
          open={patronOrderError ? true : false}
          PaperComponent={(props) => (
            <Paper {...props} sx={{ borderRadius: '18px' }} />
          )}
          fullWidth={true}
        >
          <NetworkErrorPage statusIcon={<SneakerIcon />} />
        </Dialog>
      )}

      {vendorOrders?.map((vendorOrder: VendorOrder, index) => {
        // Get the navigation image for this vendor order from the map created above
        const navigationImage =
          vendorOrderNavigationMap.get(vendorOrder._id) || '';
        const id = vendorOrder._id;
        const vendorOrderNo = vendorOrder.isOrderUpdated
          ? `#${vendorOrder?.orderNumber} (Partial)`
          : `#${vendorOrder?.orderNumber}`;

        const previousStatus =
          vendorOrder.history && vendorOrder.history.length > 1
            ? vendorOrder.history[vendorOrder.history.length - 2].status
            : undefined;
        return (
          <>
            <Grid item xs={12} key={id} p={2}>
              <OrderTrackingInfo
                order={vendorOrder}
                orderNo={vendorOrderNo}
                orderID={orderID}
                orderStatus={vendorOrder?.orderStatus}
                isClickAndCollectEnabled={order?.isClickAndCollectEnabled}
                serviceMethod={vendorOrder?.serviceMethod}
                orderBannerImage={venue?.venueLandscapeImageUrl}
                index={index}
                collectionPointLabel={selectedCollectionPoint?.label}
                previousStatusHistory={previousStatus}
              />
            </Grid>

            <Grid item xs={12}>
              <Box width={'100%'} sx={{ mb: 1 }}>
                <Border borderStyle={'dashed'} color={'#B5B5B5'} />
                <VendorActionButton
                  vendorOrder={vendorOrder}
                  vendorOrders={vendorOrders}
                  navigationImage={navigationImage}
                  setSelectedOrderNavigationImage={
                    setSelectedOrderNavigationImage
                  }
                  renderPosition="inline"
                />
              </Box>
            </Grid>
          </>
        );
      })}

      <Grid
        item
        container
        p={2}
        sx={{
          pb: { xs: 34, sm: 34, md: 34, lg: 34 },
        }}
      >
        <Grid item xs={6}>
          <Text
            text={t('common.text.total', { ns: 'common' })}
            fontWeight={700}
            fontSize={16}
            style={{
              color:
                order?.pricing?.total != order?.pricing?.initialTotal
                  ? '#D1C7C7'
                  : '#2A2A2A',
            }}
          />
          {order?.pricing?.total != order?.pricing?.initialTotal && (
            <Text text="Revised Total" fontWeight={700} fontSize={16} />
          )}
        </Grid>
        <Grid item xs={6}>
          <Box style={{ float: 'right' }}>
            {order?.pricing?.total != order?.pricing?.initialTotal && (
              <Text
                fontWeight={700}
                fontSize={16}
                text={getPriceWithCurrency(order?.pricing?.initialTotal)}
                style={{
                  textDecoration: 'line-through',
                  color:
                    order?.pricing?.total != order?.pricing?.initialTotal
                      ? '#D1C7C7'
                      : '#2A2A2A',
                }}
              />
            )}
            <Text
              fontWeight={700}
              fontSize={16}
              text={getPriceWithCurrency(
                order?.pricing?.total,
                vendorOrders[0]?.orderCurrency?.symbol,
              )}
            />
          </Box>
        </Grid>
      </Grid>

      <Grid
        item
        container
        xs={12}
        sm={8}
        md={6}
        lg={3}
        p={2}
        m={'auto'}
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1,
          background: '#FFFFFF',
          borderTopLeftRadius: '16px',
          borderTopRightRadius: '16px',
          boxShadow: '0px 6px 14px 2px #F5F5F5',
        }}
      >
        <Grid
          item
          display="flex"
          flexDirection={'column'}
          gap="24px"
          width={'100%'}
        >
          {vendorOrders.length === 1 && (
            <VendorActionButton
              vendorOrder={vendorOrders[0]}
              vendorOrders={vendorOrders}
              navigationImage={
                vendorOrderNavigationMap.get(vendorOrders[0]._id) || ''
              }
              setSelectedOrderNavigationImage={setSelectedOrderNavigationImage}
              renderPosition="bottom"
            />
          )}

          {!shouldHideHomeButton && <GlobalHomeButton />}
          {!canCancel && (
            <Box style={{ display: 'flex', flexDirection: 'column' }}>
              <SaveAppToPhoneButton />
              <CustomerSupportButton
                style={{ marginLeft: 'auto' }}
              ></CustomerSupportButton>
            </Box>
          )}
        </Grid>

        {canCancel && (
          <Grid item mt={2} width={'100%'}>
            <Button
              onClick={cancelOrderHandler}
              variant="outlined"
              fullWidth={true}
              sx={{
                marginY: '10px',
                width: '98%',
                height: '48px',
                fontWeight: '600',
                fontSize: '18px',
                WebkitBorderRadius: '30px',
                textTransform: 'none',
                p: '8px',
                color: 'red',
                borderRadius: '30px',
                backgroundColor: 'white',
                boxShadow: '0px 0px 0px 8px white',
                borderColor: 'red',
                border: '1.5px solid red',
                '&:focus': {
                  border: '1px solid red',
                },
              }}
            >
              {t('patron_portal.order.labelCancelButton', {
                ns: 'patron_portal',
              })}
            </Button>
            <Box textAlign={'center'}>
              <Text
                fontSize={14}
                fontColor="red"
                text={t('patron_portal.order.msgCancelButtonAvailability', {
                  ns: 'patron_portal',
                })}
              />
            </Box>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};

const DirectionModal = ({
  isModalOpen,
  onClose,
  image,
}: {
  isModalOpen: boolean;
  onClose: () => void;
  image: string;
}) => {
  return (
    <>
      <Drawer
        anchor="bottom"
        open={isModalOpen}
        onClose={onClose}
        disableAutoFocus={true}
        closeAfterTransition
        sx={{
          '& .MuiDrawer-paper': {
            borderTopLeftRadius: '24px',
            borderTopRightRadius: '24px',
            backgroundColor: 'rgba(255, 255, 255, 0.0)',
            maxHeight: '80%',
            overflowY: 'auto',
            boxShadow: 0,
          },
        }}
      >
        <Box display={'flex'} justifyContent={'center'}>
          <IconButton
            onClick={onClose}
            sx={{
              position: 'relative',
            }}
          >
            <CloseRoundedIcon
              sx={{
                fontSize: '20px',
                color: '#2A2A2A',
                cursor: 'pointer',
                padding: '2px',
                borderRadius: '48%',
                backgroundColor: 'rgba(255, 255, 255, 1)',
                boxShadow: 1,
              }}
            />
          </IconButton>
        </Box>
        <Box
          sx={{
            maxHeight: '100%',
            overflowY: 'auto',
            pointerEvents: 'auto',
            borderTopLeftRadius: '24px',
            borderTopRightRadius: '24px',
            backgroundColor: '#FAFAFA',
            overflow: 'hidden',
            paddingBottom: '30vh',
          }}
        >
          <Box
            display={'flex'}
            flexDirection={'column'}
            justifyContent={'center'}
            alignItems={'center'}
          >
            <Image
              style={{
                height: '100%',
                borderRadius: 2,
              }}
              src={image}
            />
            {/* <Box
              m={'20px'}
              display={'flex'}
              flexDirection={'column'}
              justifyContent={'center'}
              alignItems={'center'}
            >
              <Text text={'Collect From'} fontSize={32} fontWeight={900} />
              <Text text={location} fontSize={32} />
            </Box> */}
          </Box>
        </Box>
      </Drawer>
    </>
  );
};
